import { logOut } from 'common/services/auth.service';
import { getAccountAvatarUrl } from 'common/constants/Avatars';
import { fetchAndStoreNewRefreshAndAccessTokens } from 'common/services/token.service';
import * as ZendeskService from 'common/services/zendesk.service';
import axios from 'common/requester';
import { LINB_API } from 'common/constants/api-urls';
import { ACCOUNT_TYPES_MAP } from '@linearb/linjs-common';
import { disconnectPusher } from 'common/services/pusher.service';
import * as TrackingService from 'common/services/tracking.service';
import { TRACKING } from 'common/constants/constants';
import {
  LBAvatar,
  LBAvatarImage,
  LBAvatarFallback,
  LBDropdownMenu,
  LBDropdownMenuCheckboxItem,
  LBDropdownMenuContent,
  LBDropdownMenuGroup,
  LBDropdownMenuItem,
  LBDropdownMenuLabel,
  LBDropdownMenuSeparator,
  LBDropdownMenuTrigger,
  LBEditProfileIcon,
  LBLogoutIcon
} from '@linearb/buzz';

async function switchOrg(organizationId: number) {
  await fetchAndStoreNewRefreshAndAccessTokens(organizationId, true);
  disconnectPusher();
  window.location.reload();
  window.location.href = '/';
}

async function userLogOut() {
  await axios.get(LINB_API.TOKEN.LOGOUT, {
    withCredentials: true
  });
  logOut();
}

export default function UserProfile({
  account,
  patchAccount,
  setOpenUserProfileWizard,
  isUserProfileWizardEnabled
}) {
  if (!account) {
    return null;
  }
  const { id: accountId = '', organization_id: orgId = '', name } = account;
  const organizations = account?.organizations ?? [];
  const isNotificationsOn = account?.settings?.notifications?.email;
  const avatar = getAccountAvatarUrl(account);
  const accountType = account?.type_id;
  const showNotificationSwitch = accountType === ACCOUNT_TYPES_MAP.Primary;

  const initials =
    name
      ?.split(' ')
      .map(part => part.charAt(0))
      .join('') ?? '';

  function toggleNotifications(checked: boolean) {
    patchAccount(account.id, {
      settings: {
        ...account.settings,
        notifications: { email: checked }
      }
    });
  }

  return (
    <LBDropdownMenu>
      <LBDropdownMenuTrigger className='focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded-full'>
        <LBAvatar data-cy='profile-icon'>
          <LBAvatarImage src={avatar} />
          <LBAvatarFallback>{initials}</LBAvatarFallback>{' '}
        </LBAvatar>
        <LBDropdownMenuContent className='min-w-[350px] z-[999]' align='end'>
          <LBDropdownMenuLabel className='flex flex-row items-center justify-center gap-4'>
            <LBAvatar className='size-24'>
              <LBAvatarImage src={avatar} />
            </LBAvatar>

            <span className='flex flex-col items-start'>
              <h3 className='text-xl'>{account.name}</h3>
              <div className='text-base text-muted-foreground font-light'>
                {account.email}
              </div>
            </span>
          </LBDropdownMenuLabel>
          <LBDropdownMenuSeparator />

          {showNotificationSwitch && (
            <LBDropdownMenuCheckboxItem
              checked={isNotificationsOn}
              onCheckedChange={toggleNotifications}
            >
              Receive Email Notifications
            </LBDropdownMenuCheckboxItem>
          )}

          {isUserProfileWizardEnabled && (
            <LBDropdownMenuItem
              data-cy='profile-panel'
              onSelect={() => {
                TrackingService.logEvent(TRACKING.EVENTS.USER_PROFILE.EVENT, {
                  event_type:
                    TRACKING.EVENTS.USER_PROFILE.EVENT_TYPE.EDIT_USER_PROFILE,
                  user_id: accountId,
                  org_id: orgId
                });
                setOpenUserProfileWizard(true);
              }}
            >
              <LBEditProfileIcon className='size-4' />
              Edit User Profile
            </LBDropdownMenuItem>
          )}
          {organizations.length > 1 && (
            <>
              <LBDropdownMenuGroup>
                <LBDropdownMenuLabel>Switch Organization</LBDropdownMenuLabel>
                <div className='overflow-auto max-h-56'>
                  {organizations.map(
                    ({ id, name, has_valid_license: licenseValid }) => (
                      <>
                        {!licenseValid ? (
                          <LBDropdownMenuItem
                            className='text-destructive'
                            onSelect={ZendeskService.openSupportRequest}
                          >
                            {name} - click to contact support
                          </LBDropdownMenuItem>
                        ) : (
                          <LBDropdownMenuItem
                            key={id}
                            disabled={
                              !licenseValid || id === account.organization_id
                            }
                            onClick={() => {
                              return switchOrg(id);
                            }}
                          >
                            {name}
                          </LBDropdownMenuItem>
                        )}
                      </>
                    )
                  )}
                </div>
              </LBDropdownMenuGroup>
              <LBDropdownMenuSeparator />
            </>
          )}
          <LBDropdownMenuItem onSelect={userLogOut} data-cy='btn-logout'>
            <LBLogoutIcon className='size-4' />
            Log Out
          </LBDropdownMenuItem>
        </LBDropdownMenuContent>
      </LBDropdownMenuTrigger>
    </LBDropdownMenu>
  );
}
